import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent } from "@/components/ui/card";
import { SKILL_AREAS } from "./PlayerSkillRating";
import {
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
  ResponsiveContainer,
  Legend,
} from "recharts";

interface Props {
  playerId: string;
}

export default function PlayerRadarChart({ playerId }: Props) {
  const { data, isLoading } = useQuery({
    queryKey: ["player-radar", playerId],
    queryFn: async () => {
      const { data } = await supabase
        .from("player_feedback")
        .select("skill_ratings, created_at")
        .eq("player_id", playerId)
        .not("skill_ratings", "is", null)
        .order("created_at", { ascending: false })
        .limit(2);
      return data ?? [];
    },
  });

  if (isLoading) return <p className="text-sm text-muted-foreground">Laddar...</p>;

  const current = data?.[0]?.skill_ratings as Record<string, number> | null;
  const previous = data?.[1]?.skill_ratings as Record<string, number> | null;

  if (!current) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <p className="text-sm">Inga bedömningar ännu.</p>
      </div>
    );
  }

  const radarData = SKILL_AREAS.map((area) => ({
    area: area.label,
    current: current[area.key] || 0,
    ...(previous ? { previous: previous[area.key] || 0 } : {}),
  }));

  return (
    <Card>
      <CardContent className="py-4 px-2">
        <h3 className="text-sm font-semibold text-center mb-2">Utvecklingsöversikt</h3>
        <ResponsiveContainer width="100%" height={280}>
          <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="70%">
            <PolarGrid />
            <PolarAngleAxis dataKey="area" tick={{ fontSize: 10 }} />
            <PolarRadiusAxis domain={[0, 5]} tickCount={6} tick={{ fontSize: 9 }} />
            <Radar
              name="Senaste"
              dataKey="current"
              stroke="hsl(var(--primary))"
              fill="hsl(var(--primary))"
              fillOpacity={0.25}
            />
            {previous && (
              <Radar
                name="Tidigare"
                dataKey="previous"
                stroke="hsl(var(--muted-foreground))"
                fill="hsl(var(--muted-foreground))"
                fillOpacity={0.1}
              />
            )}
            <Legend wrapperStyle={{ fontSize: 11 }} />
          </RadarChart>
        </ResponsiveContainer>
      </CardContent>
    </Card>
  );
}
